import { z } from 'zod';
import { ASSIGNABLE_ROLES } from '../roles.js';
import { emailSchema, optionalText, requiredText } from './common.js';

/**
 * Authentication and account management.
 *
 * The password rule is a length floor and nothing else. Composition rules ("one
 * capital, one symbol") measurably push people towards `Password1!` and towards
 * writing it on the monitor; length is the property that actually costs an attacker
 * anything. The 12-character floor is the one thing enforced, and it is enforced
 * server-side too — this schema is shared, but the API re-parses every body it
 * receives rather than trusting that the client did.
 */

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Usernames are at least 3 characters')
  .max(40)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, 'Use letters, numbers, dot, underscore or hyphen');

export const passwordSchema = z
  .string()
  .min(12, 'Use at least 12 characters — length matters more than symbols')
  .max(200, 'That is longer than the hashing routine accepts');

export const loginSchema = z.object({
  username: z.string().trim().toLowerCase().min(1, 'Enter your username').max(40),
  password: z.string().min(1, 'Enter your password').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const createUserSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
  name: requiredText(120, 'Full name'),
  email: emailSchema.optional().nullable(),
  role: z.enum(ASSIGNABLE_ROLES),
  isActive: z.boolean().default(true),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/**
 * Updating a user never carries a password — `PUT /users/:id/password` is a separate
 * endpoint. Folding the two together means every form that edits a display name has to
 * be trusted with credentials, and an accidental empty string in a shared payload
 * silently rotates someone's password.
 */
export const updateUserSchema = z.object({
  name: requiredText(120, 'Full name'),
  email: emailSchema.optional().nullable(),
  role: z.enum(ASSIGNABLE_ROLES),
  isActive: z.boolean(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const setPasswordSchema = z.object({
  password: passwordSchema,
});

/** Changing your own password requires proving you know the current one. */
export const changeOwnPasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'The new password must differ from the current one',
    path: ['newPassword'],
  });

export const updateOwnProfileSchema = z.object({
  name: requiredText(120, 'Full name'),
  email: emailSchema.optional().nullable(),
});

/** What `GET /auth/me` returns and what the JWT carries. Never the password hash. */
export interface AuthUser {
  id: number;
  username: string;
  name: string;
  email: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
}

export const userListQuerySchema = z.object({
  search: optionalText(120),
  role: z.enum(ASSIGNABLE_ROLES).optional(),
  isActive: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
